// The inbox as a hook — one source for the bell and the Alerts page.
//
// Fetches the caller's rows, then keeps them fresh three ways: the server's
// per-user push ('notifications-changed' on the shared socket), a refetch
// when the tab regains focus, and a slow poll as the fallback for a socket
// that never connected. Marking read is optimistic — the badge drops at
// once, the server confirms the count.

import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@app/services/api';
import { socket } from '@platform/http/socket';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';

const POLL_MS = 60_000;

export const KIND_VERB = {
  mention: 'mentioned you on',
  assigned: 'assigned you',
  commented: 'commented on',
  transitioned: 'moved',
};

export function issueTabUrl(issueKey) {
  const params = new URLSearchParams({ jql: `key = ${issueKey}` });
  return '/issues?' + params.toString();
}

export function relativeTime(iso, now = Date.now()) {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function useNotifications({ limit = 50, unreadOnly = false } = {}) {
  const { user } = useAuth();
  const accountId = user?.accountId ? String(user.accountId) : null;
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api.getNotifications({ limit, unreadOnly });
      if (!alive.current) return;
      setItems(Array.isArray(data.items) ? data.items : []);
      setUnread(Number(data.unread) || 0);
      setError(null);
    } catch (err) {
      if (alive.current) setError(err);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [limit, unreadOnly]);

  useEffect(() => {
    alive.current = true;
    refresh();
    const onPush = (payload) => {
      if (!payload || (accountId && payload.accountId && String(payload.accountId) !== accountId)) return;
      refresh();
    };
    const onFocus = () => { if (document.visibilityState === 'visible') refresh(); };
    if (!socket.connected) socket.connect();
    socket.on('notifications-changed', onPush);
    socket.on('connect', refresh);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    const timer = setInterval(refresh, POLL_MS);
    return () => {
      alive.current = false;
      socket.off('notifications-changed', onPush);
      socket.off('connect', refresh);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      clearInterval(timer);
    };
  }, [refresh, accountId]);

  const markRead = useCallback(async (ids) => {
    const set = new Set((ids || []).map(String));
    if (!set.size) return;
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (set.has(String(n.id)) && !n.read_at ? { ...n, read_at: now } : n)));
    setUnread((u) => Math.max(0, u - set.size));
    try {
      const data = await api.markNotificationsRead([...set]);
      if (alive.current && data && Number.isFinite(data.unread)) setUnread(data.unread);
    } catch { refresh(); }
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));
    setUnread(0);
    try { await api.markAllNotificationsRead(); } catch { refresh(); }
  }, [refresh]);

  return { items, unread, loading, error, refresh, markRead, markAllRead };
}
